/**
 * Uniview PoE switch catalog — NSW2020 (unmanaged) + NSW2010 (rack
 * unmanaged) + NSW3000 (cloud-managed) + ISW5000 (industrial).
 *
 * Uniview NVRs are not here — they are already covered in
 * midTierNVRCatalog.ts. This file adds only the switch lineup.
 *
 * All specs verified against uniview.com / global.uniview.com product
 * pages on 2026-05-15. URL cited per entry. Missing specs left
 * undefined.
 *
 * `cloud-managed` (Uniview's own term for NSW3000) is mapped to
 * `smart-managed` since the schema doesn't have a cloud-managed
 * variant.
 */

import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const UNIVIEW_SWITCH_CATALOG: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────
  // NSW2020 — unmanaged 100M PoE+ access switches
  // ─────────────────────────────────────────────────────────────

  {
    id: 'uniview-nsw2020-6t-poe-in',
    manufacturer: 'Uniview',
    model: 'NSW2020-6T-POE-IN',
    category: 'poe-switch',
    tagline: 'Compact 6-port 100M PoE+ desktop switch for small camera sites',
    verified: true,
    datasheetUrl: 'https://www.uniview.com/Products/Transmission/Switches/Access_Switches/NSW2020-6T-POE-IN/',
    specs: {
      totalPorts: 6,
      poePorts: 4,
      poeBudgetW: 60,
      poeStandard: '802.3at',
      poePerPortW: 30,
      switchingCapacityGbps: 1.2,
      management: 'unmanaged',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  {
    id: 'uniview-nsw2020-10t-poe-in',
    manufacturer: 'Uniview',
    model: 'NSW2020-10T-POE-IN',
    category: 'poe-switch',
    tagline: 'Fanless 10-port 100M PoE+ switch with 8 PoE ports',
    verified: true,
    datasheetUrl: 'https://www.uniview.com/Products/Transmission/Switches/Access_Switches/NSW2020-10T-POE-IN/',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 88,
      poeStandard: '802.3at',
      poePerPortW: 30,
      switchingCapacityGbps: 2,
      management: 'unmanaged',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  {
    id: 'uniview-nsw2020-16t1gt1gc-poe-in',
    manufacturer: 'Uniview',
    model: 'NSW2020-16T1GT1GC-POE-IN',
    category: 'poe-switch',
    tagline: '16-port 100M PoE+ access switch with gigabit uplinks',
    verified: true,
    datasheetUrl: 'https://www.uniview.com/Products/Transmission/Switches/Access_Switches/NSW2020-16T1GT1GC-POE-IN/',
    specs: {
      totalPorts: 18,
      poePorts: 16,
      poeBudgetW: 250,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '1× 1G RJ45 + 1× 1G Combo',
      switchingCapacityGbps: 7.2,
      management: 'unmanaged',
      formFactor: 'desktop',
      fanless: false,
    },
  },

  {
    id: 'uniview-nsw2020-24t1gt1gc-poe-in',
    manufacturer: 'Uniview',
    model: 'NSW2020-24T1GT1GC-POE-IN',
    category: 'poe-switch',
    tagline: '24-port 100M PoE+ rackmount with 370 W budget for IP cam runs',
    verified: true,
    datasheetUrl: 'https://www.uniview.com/Products/Transmission/Switches/Access_Switches/NSW2020-24T1GT1GC-POE-IN/',
    specs: {
      totalPorts: 26,
      poePorts: 24,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '1× 1G RJ45 + 1× 1G Combo',
      switchingCapacityGbps: 8.8,
      management: 'unmanaged',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // NSW2010 — rackmount unmanaged PoE+ (390 W)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'uniview-nsw2010-24t2gc-poe-in',
    manufacturer: 'Uniview',
    model: 'NSW2010-24T2GC-POE-IN',
    category: 'poe-switch',
    tagline: '24-port 100M PoE+ rack switch with 390 W budget and dual combo uplinks',
    verified: true,
    datasheetUrl: 'https://www.uniview.com/Products/Transmission/Switches/Access_Switches/NSW2010-24T2GC-PoE-IN/',
    specs: {
      totalPorts: 26,
      poePorts: 24,
      poeBudgetW: 390,
      poeStandard: '802.3at',
      uplinks: '2× 1G Combo',
      switchingCapacityGbps: 8.8,
      management: 'unmanaged',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // NSW3000 — cloud-managed (mapped to smart-managed)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'uniview-nsw3000-8t2gt-poe-in',
    manufacturer: 'Uniview',
    model: 'NSW3000-8T2GT-POE-IN',
    category: 'poe-switch',
    tagline: 'Fanless cloud-managed 8-port PoE+ switch with gigabit uplinks',
    verified: true,
    datasheetUrl: 'https://www.uniview.com/Products/Transmission/Switches/Cloud_Managed_Switches/NSW3000-8T2GT-POE-IN/',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 120,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 1G RJ45',
      switchingCapacityGbps: 5.6,
      management: 'smart-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  {
    id: 'uniview-nsw3000-16t1gt1gc-poe-in',
    manufacturer: 'Uniview',
    model: 'NSW3000-16T1GT1GC-POE-IN',
    category: 'poe-switch',
    tagline: 'Cloud-managed 16-port 100M PoE+ rack switch',
    verified: true,
    datasheetUrl: 'https://www.uniview.com/Products/Transmission/Switches/Cloud_Managed_Switches/NSW3000-16T1GT1GC-POE-IN/',
    specs: {
      totalPorts: 18,
      poePorts: 16,
      poeBudgetW: 250,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '1× 1G RJ45 + 1× 1G Combo',
      switchingCapacityGbps: 7.2,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'uniview-nsw3000-17gt1gp1gc-poe-in',
    manufacturer: 'Uniview',
    model: 'NSW3000-17GT1GP1GC-POE-IN',
    category: 'poe-switch',
    tagline: 'All-gigabit cloud-managed 16-port PoE+ switch with SFP uplink',
    verified: true,
    datasheetUrl: 'https://www.uniview.com/Products/Transmission/Switches/Cloud_Managed_Switches/NSW3000-17GT1GP1GC-POE-IN/',
    specs: {
      totalPorts: 18,
      poePorts: 16,
      poeBudgetW: 250,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '1× 1G SFP + 1× 1G Combo',
      switchingCapacityGbps: 38,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'uniview-nsw3000-24t1gt1gc-poe-in',
    manufacturer: 'Uniview',
    model: 'NSW3000-24T1GT1GC-POE-IN',
    category: 'poe-switch',
    tagline: 'Cloud-managed 24-port 100M PoE+ rack switch for medium sites',
    verified: true,
    datasheetUrl: 'https://global.uniview.com/Products/Transmission/Switches/Cloud_Managed_Switches/NSW3000-24T1GT1GC-POE-IN/',
    specs: {
      totalPorts: 26,
      poePorts: 24,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '1× 1G RJ45 + 1× 1G Combo',
      switchingCapacityGbps: 8.8,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'uniview-nsw3000-25gt1gp1gc-poe-in',
    manufacturer: 'Uniview',
    model: 'NSW3000-25GT1GP1GC-POE-IN',
    category: 'poe-switch',
    tagline: 'All-gigabit cloud-managed 24-port PoE+ rack switch with SFP uplink',
    verified: true,
    datasheetUrl: 'https://global.uniview.com/Products/Transmission/Switches/Cloud_Managed_Switches/NSW3000-25GT1GP1GC-POE-IN/',
    specs: {
      totalPorts: 26,
      poePorts: 24,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '1× 1G SFP + 1× 1G Combo',
      switchingCapacityGbps: 54,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // ISW5000 — industrial fanless PoE+
  // ─────────────────────────────────────────────────────────────

  {
    id: 'uniview-isw5000-8gt4gp-poe-in',
    manufacturer: 'Uniview',
    model: 'ISW5000-8GT4GP-POE-IN',
    category: 'poe-switch',
    tagline: 'Industrial fanless 8-port gigabit PoE+ switch with 4× SFP uplinks',
    verified: true,
    datasheetUrl: 'https://global.uniview.com/Products/Transmission/Switches/Access_Switches/ISW5000-8GT4GP-POE-IN/',
    specs: {
      totalPorts: 12,
      poePorts: 8,
      poeBudgetW: 120,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 1G SFP',
      switchingCapacityGbps: 24,
      management: 'L2-managed',
      formFactor: 'industrial-DIN',
      fanless: true,
      operatingTempC: '-30 to +65',
    },
  },
];
